import React, { useState, useEffect } from "react";
import { useMap } from "@vis.gl/react-google-maps";
import colors from "tailwindcss/colors";
import { SurfaceType } from "./DataAndMap";
import { SurfaceIcon } from "./components/Icons";

// import reducer, {
//   useDrawingManagerEvents,
//   useOverlaySnapshots,
// } from "./toolbar-events";

import {
  HexColor,
  TerraDraw,
  TerraDrawFreehandMode,
  TerraDrawLineStringMode,
  TerraDrawPointMode,
  TerraDrawPolygonMode,
} from "terra-draw";

import { TerraDrawGoogleMapsAdapter } from "terra-draw-google-maps-adapter";
import { COLOR_BLUE } from "./utils/colors";
import { CreateMode, SimpleLatLng, SiteMapSurfaceFeatureType } from "./Core";
import { useMapEditing } from "./utils/routing";
import { useToolbarState } from "./contexts/ToolbarStateContext";
import { Tooltip } from "./Tooltip";
import { useLocation, useParams } from "react-router-dom";
import { useMapDataLayer } from "./contexts/DataLayerContext";
import { useData } from "./contexts/DataContext";
import { usePolygonHole } from "./contexts/PolygonHoleContext";
import useNavigateKeepSearchParams from "../../hooks/useNavigateKeepSearchParams";

interface MockLatLng {
  lat: () => number;
  lng: () => number;
}

interface MockPath {
  getArray: () => MockLatLng[];
}

export interface MockPolygon {
  getPath: () => MockPath;
}

export interface MockMarker {
  getPosition: () => MockLatLng;
  setMap: () => void;
}

export interface MockPolyline {
  getPath: () => MockPath;
}

interface ToolbarProps {
  onComplete: (
    type: CreateMode,
    shape: MockPolygon | MockMarker | MockPolyline,
    surfaceType?: SiteMapSurfaceFeatureType,
  ) => void;
  surfaceTypeOptions: SurfaceType[];
}

export const Toolbar = ({ onComplete, surfaceTypeOptions }: ToolbarProps) => {
  const [draw, setDraw] = useState<TerraDraw | null>(null);
  const [showFirstUseHelp, setShowFirstUseHelp] = useState<boolean>(true);
  const { editing, setEditing } = useMapEditing();
  const { selectedMode, setSelectedMode } = useToolbarState();
  const { shouldCutHole, setShouldCutHole } = usePolygonHole();
  const { surfaceData } = useMapDataLayer();
  const { isEmptyMap, boundary, undoMapChange } = useData();
  const [freehand, setFreehand] = useState(false);
  const navigate = useNavigateKeepSearchParams(["return_to"]);
  const { jobId, season, surfaceId } = useParams();
  const location = useLocation();
  const editingBoundary =
    location.pathname.endsWith("/boundary") ||
    location.pathname.endsWith("/setup-boundary");
  const editingSurface = /\/edit\/surface\/\d+$/.test(location.pathname);

  const [selectedSurfaceType, setSelectedSurfaceType] = useState<SurfaceType>(
    surfaceTypeOptions[0],
  );
  const [selectedPointType, setSelectedPointType] = useState<
    "hazard" | "text" | null
  >(null);
  const map = useMap();

  useEffect(() => {
    if (!map) return;

    const element = document.querySelector("#property-site-map > div");
    if (!element) {
      console.error("Could not find map container element");
      return;
    }
    element.id = "property-site-map-container";

    const drawLib = new TerraDraw({
      adapter: new TerraDrawGoogleMapsAdapter({
        lib: google.maps,
        map: map,
        coordinatePrecision: 9,
      }),
      modes: [
        new TerraDrawPointMode(),
        new TerraDrawFreehandMode({
          styles: {
            outlineWidth: 2,
          },
        }),
        new TerraDrawPolygonMode({
          pointerDistance: 10,
          styles: {
            outlineWidth: 2,
          },
          // we lose the more interesting snapping options because we don't keep
          // all our polygons within TerraDraw ...
          snapping: undefined,
          // {
          //   toLine: false,
          //   toCoordinate: false,
          // },
        }),
        new TerraDrawLineStringMode({
          // @ts-expect-error -- bug in types?
          lineStringColor: COLOR_BLUE.hex as HexColor,
        }),
      ],
    });

    // Start drawing
    drawLib.start(); // Turn this on / off in a useEffect that pays attention to editMode - which should turn off tools - use stop

    // Unlike other adapters, the google adapter 'ready' event is required to be listened for
    // because the adapter creates an OverlayView which is only ready asynchronously
    drawLib.on("ready", () => {
      if (!drawLib) return;
      // console.log("TerraDraw is ready, setting initial mode to static");
      setDraw(drawLib);
    });

    // Cleanup function
    return () => {
      if (drawLib) {
        // console.info("stopping drawing");
        // drawLib.stop();
      }
    };
  }, [map]); // Only re-run if the map changes

  // The following useEffect is in charge of "exiting" drawing tools if we switch out of editing mode.
  // Without this, you can be part way though editing a polygon, click to exit edit mode, and still be drawing a polygon.
  useEffect(() => {
    if (editing === false) {
      draw?.setMode("static");
      draw?.clear();
      setSelectedPointType(null);
      setSelectedMode(null);
    }
  }, [editing, draw, setSelectedMode]);

  // Set up terra-draw event listeners
  useEffect(() => {
    if (!draw) return;

    const handleFinish = () => {
      const geometry = draw.getSnapshot()[0].geometry;

      // Handle different geometry types
      if (geometry.type === "Polygon") {
        // Convert terra-draw coordinates to Google Maps format
        const coordinates = geometry.coordinates[0].map((coord: number[]) => ({
          lat: coord[1],
          lng: coord[0],
        }));

        // Create a mock polygon object that matches the expected interface
        const mockPolygon = {
          getPath: () => ({
            getArray: () =>
              coordinates.map((coord: SimpleLatLng) => ({
                lat: () => coord.lat,
                lng: () => coord.lng,
              })),
          }),
        };

        onComplete("polygon", mockPolygon, selectedSurfaceType?.value);
      } else if (geometry.type === "Point") {
        // Handle point/marker creation
        const position = geometry.coordinates;

        // Create a mock marker object that matches the expected interface
        const mockMarker = {
          getPosition: () => ({
            lat: () => position[1],
            lng: () => position[0],
          }),
          setMap: () => {}, // Mock function for compatibility
        };

        if (selectedPointType) {
          onComplete(selectedPointType, mockMarker);
        }
      } else if (geometry.type === "LineString") {
        // Handle line/arrow creation
        const coordinates = geometry.coordinates.map((coord: number[]) => ({
          lat: coord[1],
          lng: coord[0],
        }));

        // Create a mock polyline object
        const mockPolyline = {
          getPath: () => ({
            getArray: () =>
              coordinates.map((coord: SimpleLatLng) => ({
                lat: () => coord.lat,
                lng: () => coord.lng,
              })),
          }),
        };

        onComplete("arrow", mockPolyline);
      }

      // now that we've passed the data up, trash it for our drawing manager
      draw.setMode("static");
      draw.clear();
      setSelectedPointType(null);
      setSelectedMode(null);
    };

    // Listen for drawing completion events
    draw.on("finish", handleFinish);

    // Cleanup event listener
    return () => {
      draw.off("finish", handleFinish);
    };
  }, [
    draw,
    onComplete,
    selectedPointType,
    selectedSurfaceType,
    setSelectedMode,
  ]);

  const isActive = (mode: "arrow" | "hazard" | "surface" | "text") => {
    if (mode === "arrow" && selectedMode === "arrow") return true;
    if (mode === "hazard" && selectedMode === "hazard") return true;
    if (mode === "surface" && selectedMode === "surface") return true;
    if (mode === "text" && selectedMode === "text") return true;

    return false;
  };

  const changeDrawingMode = (
    mode: "polygon" | "point" | "arrow" | "",
    secondaryType:
      | "hazard"
      | "text"
      | "cut"
      | SiteMapSurfaceFeatureType
      | null = null,
    resetNavigation: boolean = true,
  ) => {
    if (!draw) return;

    // if we're engaging with editing tools, switch to editing mode
    if (!editing) {
      const extra =
        window.location.pathname.match(
          /site-map\/(snow|landscape)\/?(.*)/,
        )?.[2] ?? "";
      setEditing(true, extra ? `/${extra}` : "");
    }
    setShowFirstUseHelp(false);

    // Set terra-draw mode
    if (mode === "polygon" && secondaryType) {
      const cutType: SurfaceType = {
        color: "#000000",
        label: "Cut",
        value: "hole",
      };
      // Find the surface type option that matches the secondary type
      const surfaceOption =
        secondaryType === "cut"
          ? cutType
          : surfaceTypeOptions.find((option) => option.value === secondaryType);

      if (surfaceOption && selectedPointType !== secondaryType) {
        const terradrawMode = freehand ? "freehand" : "polygon";
        draw.setMode(terradrawMode); // move into polygon mode if choosing polygon mode

        draw.updateModeOptions(terradrawMode, {
          styles: {
            fillColor: surfaceOption.color as HexColor,
            outlineColor: surfaceOption.color as HexColor,
          },
        });

        setSelectedMode("surface");
        setSelectedSurfaceType(surfaceOption);
      } else {
        draw.setMode("static");
        setSelectedMode(null);
        setSelectedPointType(null);
      }
    } else if (mode === "point") {
      let internalMode: "hazard" | "text" | null = null;
      if (secondaryType === "hazard") {
        internalMode = "hazard";
      }
      if (secondaryType === "text") {
        internalMode = "text";
      }
      if (selectedPointType !== secondaryType) {
        draw.setMode(mode); // force re-entry into point mode if changing point types
        setSelectedMode(internalMode);
      } else {
        draw.setMode("static");
      }
    } else if (mode === "arrow") {
      draw.setMode("linestring");
      setSelectedMode(mode);
      setSelectedPointType(null);
    } else if (mode === "") {
      // Stop drawing and clear any active drawing
      draw.setMode("static");
      setSelectedMode(null);
      setSelectedPointType(null);
    }

    if (
      mode === "point" &&
      secondaryType &&
      (secondaryType === "hazard" || secondaryType === "text")
    ) {
      setSelectedPointType(secondaryType);
    }
    if (resetNavigation) {
      // console.log(
      //   "Triggering navigation since we are not cutting holes",
      //   surfaceId,
      //   shouldCutHole,
      // );
      navigate(`/admin/property/${jobId}/site-map/${season}/edit`);
    }
  };

  // const handleSurfaceTypeChange = (selectedOption: SurfaceType | null) => {
  //   if (selectedOption) {
  //     changeDrawingMode("polygon", selectedOption.value);
  //   }
  // };

  return (
    <div className="site-map-toolbar m-2 bg-white bg-opacity-90 rounded-xl text-base p-2 flex gap-3 w-full relative">
      <div className="flex gap-1 border-r border-r-gray-400 pr-3">
        {surfaceTypeOptions.map((st) => (
          <ToolbarBtn
            // It's important to disable surfaces while we are editing boundaries.  Otherwise we can get a boundary created as both a boundary and surface (by clicking on this button while editing a brand new boundary).
            disabled={editingBoundary}
            key={st.value}
            tooltip={
              <>
                <h3>{st.label}</h3>
                <p>
                  Draw {st.label} by clicking on the map. Double click to
                  finish.
                </p>
              </>
            }
            active={
              isActive("surface") && selectedSurfaceType.value == st.value
            }
            onClick={() => {
              if (
                isActive("surface") &&
                selectedSurfaceType.value == st.value
              ) {
                changeDrawingMode("");
              } else {
                changeDrawingMode("polygon", st.value);
              }
            }}
          >
            <span className="flex text-xs items-center gap-1 pr-[2px]">
              <ToolbarSurfaceIcon color={st.color} />
              {st.label}
            </span>
          </ToolbarBtn>
        ))}
        <ToolbarBtn
          tooltip={
            <>
              <h3>Cut Holes</h3>
              <p>
                Cut out holes in areas for trees, islands, etc. You must have an
                area selected on the map before using this tool.
              </p>
              {surfaceId == null ? (
                <p className="font-bold mt-2">
                  Select an area to enable this tool.
                </p>
              ) : null}
            </>
          }
          active={shouldCutHole}
          disabled={surfaceId == null || editingBoundary}
          onClick={() => {
            if (shouldCutHole) {
              // disable
              setShouldCutHole(false);
              changeDrawingMode("");
            } else {
              // enable
              setShouldCutHole(true);
              changeDrawingMode("polygon", "cut", false);
            }
          }}
        >
          <svg
            width="20"
            height="20"
            data-name="Layer 1"
            version="1.1"
            viewBox="-5 -10 90.164 90.271"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="m21.875-10 15.314 42.342-42.189-15.469c0.36035 3.8836 2.0287 9.8232 6.1758 11.816l39.975 14.604 3.5703 9.8691c0.32359 1.1397 0.09924 2.1129-0.91797 2.5742a12.404 12.404 0 0 0-9.8379 12.131 12.404 12.404 0 0 0 12.406 12.404 12.404 12.404 0 0 0 12.404-12.404 12.404 12.404 0 0 0-1.2051-5.3027l-5.5078-15.285 15.346 5.6055a12.404 12.404 0 0 0 5.2988 1.2266 12.404 12.404 0 0 0 12.457-12.35 12.404 12.404 0 0 0-12.35-12.459 12.404 12.404 0 0 0-12.174 9.7852c-0.46575 1.0152-1.4398 1.2348-2.5781 0.90625l-9.9395-3.6445-14.459-40.123c-1.9752-4.1557-7.9071-5.8493-11.789-6.2266zm50.908 46.18a5.5281 5.5281 0 0 1 5.5039 5.5527 5.5281 5.5281 0 0 1-5.5508 5.5039 5.5281 5.5281 0 0 1-5.5059-5.5527 5.5281 5.5281 0 0 1 5.5527-5.5039zm-28.285 1.2168a2.0329 2.0329 0 0 1 2.0332 2.0332 2.0329 2.0329 0 0 1-2.0332 2.0332 2.0329 2.0329 0 0 1-2.0332-2.0332 2.0329 2.0329 0 0 1 2.0332-2.0332zm1.873 24.941a5.5281 5.5281 0 0 1 5.5273 5.5293 5.5281 5.5281 0 0 1-5.5273 5.5273 5.5281 5.5281 0 0 1-5.5293-5.5273 5.5281 5.5281 0 0 1 5.5293-5.5293z"
              fill="currentColor"
            />
          </svg>
        </ToolbarBtn>
      </div>
      <div className="flex gap-1 border-r border-r-gray-400 pr-3">
        <ToolbarBtn
          tooltip="Add text labels to the map"
          disabled={editingBoundary}
          active={isActive("text")}
          onClick={() =>
            changeDrawingMode(isActive("text") ? "" : "point", "text")
          }
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 640 640"
            width="20"
            height="20"
          >
            <path
              d="M349.5 115.7C344.6 103.8 332.9 96 320 96C307.1 96 295.4 103.8 290.5 115.7C197.2 339.7 143.8 467.7 130.5 499.7C123.7 516 131.4 534.7 147.7 541.5C164 548.3 182.7 540.6 189.5 524.3L221.3 448L418.6 448L450.4 524.3C457.2 540.6 475.9 548.3 492.2 541.5C508.5 534.7 516.2 516 509.4 499.7C496.1 467.7 442.7 339.7 349.4 115.7zM392 384L248 384L320 211.2L392 384z"
              fill="currentColor"
            />
          </svg>
        </ToolbarBtn>
        <ToolbarBtn
          tooltip="Draw arrows on the map. Double click to finish."
          disabled={editingBoundary}
          active={isActive("arrow")}
          onClick={() => changeDrawingMode(isActive("arrow") ? "" : "arrow")}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 640 640"
            height="20"
            width="20"
          >
            <path
              d="M566.6 342.6C579.1 330.1 579.1 309.8 566.6 297.3L406.6 137.3C394.1 124.8 373.8 124.8 361.3 137.3C348.8 149.8 348.8 170.1 361.3 182.6L466.7 288L96 288C78.3 288 64 302.3 64 320C64 337.7 78.3 352 96 352L466.7 352L361.3 457.4C348.8 469.9 348.8 490.2 361.3 502.7C373.8 515.2 394.1 515.2 406.6 502.7L566.6 342.7z"
              fill="currentColor"
            />
          </svg>
        </ToolbarBtn>
        <ToolbarBtn
          tooltip="Add hazard markers to the map (i.e. fire hydrant)"
          disabled={editingBoundary}
          active={isActive("hazard")}
          onClick={() =>
            changeDrawingMode(isActive("hazard") ? "" : "point", "hazard")
          }
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 640 640"
            height="20"
            width="20"
          >
            <path
              d="M144 88C144 74.7 133.3 64 120 64C106.7 64 96 74.7 96 88L96 552C96 565.3 106.7 576 120 576C133.3 576 144 565.3 144 552L144 452L224.3 431.9C265.4 421.6 308.9 426.4 346.8 445.3C391 467.4 442.3 470.1 488.5 452.7L523.2 439.7C535.7 435 544 423.1 544 409.7L544 130C544 107 519.8 92 499.2 102.3L489.6 107.1C443.3 130.3 388.8 130.3 342.5 107.1C307.4 89.5 267.1 85.1 229 94.6L144 116L144 88zM144 165.5L240.6 141.3C267.6 134.6 296.1 137.7 321 150.1C375.9 177.5 439.7 179.8 496 156.9L496 398.7L471.6 407.8C437.9 420.4 400.4 418.5 368.2 402.4C320 378.3 264.9 372.3 212.6 385.3L144 402.5L144 165.5z"
              fill="currentColor"
            />
          </svg>
        </ToolbarBtn>
      </div>
      <div className="flex gap-1 border-r">
        <ToolbarBtn
          tooltip={
            <>
              <h3>Geo Fence (Beta)</h3>
              <p>
                Geo Fencing is coming! Get started now by outlining the property
                boundary. Click on this tool again to finish.
              </p>
            </>
          }
          active={editingBoundary}
          onClick={() => {
            if (editingBoundary) {
              navigate(`/admin/property/${jobId}/site-map/${season}/edit`);
            } else {
              navigate(
                `/admin/property/${jobId}/site-map/${season}/edit/${boundary ? "boundary" : "setup-boundary"}`,
              );
            }
          }}
        >
          <svg
            width="20"
            height="20"
            version="1.1"
            viewBox="0 0 35.466 36.34"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="m17.922 1.0568a10.489 10.489 0 0 0-10.489 10.489 10.489 10.489 0 0 0 0.94723 4.332c-0.0036-1e-3 -0.0072-2e-3 -0.01085-3e-3 1.2467 3.0507 5.978 10.669 9.4806 15.209 2.8281-3.7247 6.2697-8.6803 9.2806-14.539a10.489 10.489 0 0 0 0.24236-0.47284c0.03208-0.0634 0.06466-0.12549 0.09664-0.18913-0.0045 1e-3 -0.0089 2e-3 -0.01344 3e-3a10.489 10.489 0 0 0 0.95447-4.3408 10.489 10.489 0 0 0-10.489-10.489zm0 5.4095a5.0791 5.0791 0 0 1 5.0793 5.0793 5.0791 5.0791 0 0 1-5.0793 5.0793 5.0791 5.0791 0 0 1-5.0793-5.0793 5.0791 5.0791 0 0 1 5.0793-5.0793z"
              fill="currentColor"
            />
            <path
              d="m24.064 28.623c2.6098-0.0708 10.342 1.6101 10.489 3.4963 2.4e-5 1.8266-7.5305 3.3073-16.82 3.3073-9.2894 0-16.82-1.4807-16.82-3.3073-0.059203-2.22 7.3354-3.2024 10.772-3.2128"
              fill="none"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.826"
            />
            <path d="m17.327 0.014145c-8.2675-0.029-13.882 10.455-9.4023 17.371 2.7336 5.4994 6.2518 10.56 9.9238 15.467 4.2976-5.7979 8.6435-11.784 11.361-18.492 1.5485-6.4538-3.0709-13.663-9.8418-14.268-0.67674-0.0816-1.3598-0.11318-2.041-0.0781zm0.73242 7.5137c4.8452-0.31757 5.0529 8.155 0.11328 8.0566-4.7236 0.61615-5.8839-7.4832-0.99805-7.9902 0.29146-0.0538 0.58854-0.0758 0.88476-0.0664z" />
          </svg>
        </ToolbarBtn>
        <ToolbarBtn
          tooltip={
            <>
              <h3>Undo</h3>
            </>
          }
          active={false}
          onClick={() => {
            undoMapChange();
          }}
        >
          <svg
            width="20"
            height="20"
            version="1.1"
            viewBox="0 0 290.11 278.08"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="m106.43 0-106.13 106.13c-0.20244 0.20244-0.30386 0.46651-0.30386 0.7307s0.10143 0.52828 0.30386 0.73071l106.13 106.13v-69.439h89.472c0.20251 0 0.38916-0.0603 0.54829-0.1602 0.16136 0.0542 0.32275 0.10889 0.48524 0.1602 58.964 3.0238 81.643 132.29-37.042 133.8 184.45-0.75595 150.43-207.89 37.042-208.64-0.16633 0.05021-0.32947 0.10287-0.49403 0.15451-0.15729-0.09656-0.34065-0.15451-0.5395-0.15451h-89.472z" />
          </svg>
        </ToolbarBtn>
      </div>
      {isEmptyMap && showFirstUseHelp ? (
        <div
          className="tooltip-text"
          style={{
            visibility: "visible",
            zIndex: 90,
            display: "flex",
            flexDirection: "column",
            left: "140px",
          }}
        >
          <h3>Get Started!</h3>
          <p className="text-left">
            Get started by choosing a surface type (Parking Lot, Snow Pile,
            etc).
          </p>
          <p className="text-left mt-2">
            Or checkout our{" "}
            <a
              className="underline"
              href="https://www.youtube.com/watch?v=IHAHAOKQVKE"
            >
              help video
            </a>
            .
          </p>
          <button
            onClick={() => setShowFirstUseHelp(false)}
            className="border block p-2 mt-4 bg-[#04a072] text-white"
          >
            Got it
          </button>
        </div>
      ) : null}
      {(isActive("surface") && !editingBoundary) || isActive("arrow") ? (
        <HelpBubble tip="Click to start. Double click to finish." />
      ) : null}
      {isActive("text") || isActive("hazard") ? (
        <HelpBubble tip="Click on the map to place." />
      ) : null}
      {isActive("surface") && editingBoundary ? (
        <HelpBubble tip="Drag the corner or middle dots. Click the geo-fence icon above to finish." />
      ) : null}
      {selectedMode === null && editingSurface ? (
        <HelpBubble tip="Drag the white dots to adjust the surface. Right click on a corner to delete." />
      ) : null}
    </div>
  );
};

const HelpBubble = ({ tip }: { tip: string }) => {
  return (
    <div
      style={{
        position: "absolute",
        top: "120%",
        left: "50%",
        width: "400px",
        textAlign: "center",
        marginLeft: "-200px",
        backgroundColor: "white",
        paddingTop: "3px",
        paddingBottom: "3px",
        opacity: 0.8,
        zIndex: 50,
        fontWeight: "bold",
        borderRadius: "5px",
        display: "flex",
        alignItems: "center",
      }}
    >
      <div className="px-2">&#128161;</div>
      <div className="text-left">{tip}</div>
    </div>
  );
};

const ToolbarBtn = ({
  disabled = false,
  active,
  tooltip,
  onClick,
  children,
}: {
  disabled?: boolean;
  active: boolean;
  tooltip: React.ReactNode;
  onClick: () => void;
  children: React.ReactNode;
}) => {
  const bg = active ? "bg-gray-400" : "bg-white";
  const hover = active
    ? "hover:bg-gray-500 hover:border-gray-600"
    : "hover:bg-gray-200 hover:border-gray-600";
  const border = active ? "border-gray-500" : "border-gray-400";
  const fontColor = active ? "text-snow-white" : "text-snow-primary";

  return (
    <Tooltip tooltip={tooltip}>
      <button
        className={`${bg} ${fontColor} ${border} ${hover} border-[1px] rounded p-1 min-w-7 ${disabled ? "opacity-30" : ""}`}
        onClick={onClick}
        disabled={disabled}
      >
        {children}
      </button>
    </Tooltip>
  );
};

export const ToolbarSurfaceIcon = ({ color }: { color?: string }) => {
  const style = {
    cursor: "pointer",
    width: "20px",
    height: "20px",
  };

  return (
    <span style={style}>
      <SurfaceIcon color={color ?? colors.gray[500]} />
    </span>
  );
};

// interface PolygonChooserProps {
//   active: boolean;
//   options: SurfaceType[];
//   selectedSurfaceType: SurfaceType | null;
//   onChange: (selectedOption: SurfaceType | null) => void;
// }

// const Control = ({ children, ...props }: ControlProps<SurfaceType, false>) => {
//   const value = props.getValue()[0];

//   return (
//     <components.Control {...props}>
//       <ToolbarSurfaceIcon color={value?.color} />
//       {children}
//     </components.Control>
//   );
// };

// const Option = (props: OptionProps<SurfaceType>) => {
//   const { children, data } = props;

//   const flexStyle = {
//     display: "flex",
//     alignItems: "center",
//     gap: "8px",
//   } as const;

//   return (
//     <components.Option {...props}>
//       <div style={flexStyle}>
//         <ToolbarSurfaceIcon color={data?.color} />
//         {children}
//       </div>
//     </components.Option>
//   );
// };

// const PolygonChooser = ({
//   active,
//   options: surfaceTypeOptions,
//   selectedSurfaceType,
//   onChange: handleSurfaceTypeChange,
// }: PolygonChooserProps) => {
//   const classNames: ClassNamesConfig<SurfaceType, false> = {
//     control: () => "pl-2 border-gray-400 text-sm bg-white leading-4",
//     menu: () => "text-sm",
//   };
//   const styles: StylesConfig<SurfaceType, false> = {
//     control: (css) => ({
//       ...css,
//       minHeight: "20px",
//       background: active ? colors.gray[400] : colors.white,
//       borderColor: colors.gray[active ? 500 : 400], // Using Tailwind's color palette
//       color: active ? colors.white : colors.black,
//     }),
//     dropdownIndicator: (css) => ({
//       ...css,
//       padding: "4px",
//       color: active ? colors.white : colors.black,
//     }),
//     singleValue: (css) => ({
//       ...css,
//       color: active ? colors.white : colors.black,
//     }),
//   };
//
//   return (
//     <>
//       <Select
//         components={{ Control, Option }}
//         isSearchable={false}
//         placeholder="Select Surface Type"
//         options={surfaceTypeOptions}
//         value={selectedSurfaceType}
//         onChange={handleSurfaceTypeChange}
//         classNames={classNames}
//         styles={styles}
//       />
//     </>
//   );
// };
